"use client";

import React from "react";
import { ChevronDown, GitCommitHorizontal } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const executionSteps = [{
  id: "01",
  title: "Diagnose",
  description: "Identifying high-impact opportunities and systemic bottlenecks in your current data architecture."
}, {
  id: "02",
  title: "Architect",
  description: "Designing robust, AI-native frameworks and enterprise platforms tailored for scalability and security."
}, {
  id: "03",
  title: "Deploy",
  description: "Precision engineering and rapid integration of intelligent systems into your core operations."
}, {
  id: "04",
  title: "Scale",
  description: "Continuous optimization and performance monitoring to ensure sustainable growth and measurable ROI."
}, {
  id: "05",
  title: "Govern",
  description: "Implementing rigorous security and alignment protocols via our integrated Cytrusst partnership."
}];
export default function OurExecutionModel() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: timelineRef,
    isVisible: isTimelineVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="147676" id="transformation-lifecycle" className="py-24 bg-background text-foreground overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/3 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-chart-2/3 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-primary/5 text-primary text-xs font-mono uppercase tracking-widest mb-6">
            <GitCommitHorizontal className="w-4 h-4" />
            <span>Process Methodology</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter mb-6 text-foreground">
            The Path to Performance
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed">
            A systematic approach to engineering high-performance enterprises through AI-first transformation and business-aligned architecture.
          </p>
        </div>

        {}
        <div ref={timelineRef} className={`hidden md:block relative mt-32 transition-all duration-1000 delay-200 transform ${isTimelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="absolute top-6 left-[10%] right-[10%] h-[2px] border-t-2 border-dashed border-primary/20 z-0" />

          <div className="grid grid-cols-5 gap-8 relative z-10">
            {executionSteps.map((step, i) => <div key={step.id} data-index={i} className="flex flex-col items-center text-center relative group">
                {}
                <span className="absolute -top-20 left-1/2 -translate-x-1/2 text-[8rem] font-mono font-black text-primary/5 transition-colors duration-500 group-hover:text-primary/10 select-none pointer-events-none leading-none">
                  {step.id}
                </span>

                {}
                <div className="w-12 h-12 rounded-sm bg-background border-4 border-primary flex items-center justify-center mb-6 shadow-sm z-10 transition-transform duration-300 group-hover:scale-105">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                </div>

                {}
                <h3 className="text-xl font-bold mb-3 text-foreground group-hover:text-primary transition-colors duration-300 tracking-tight">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed px-2">
                  {step.description}
                </p>
              </div>)}
          </div>
        </div>

        {}
        <div className="md:hidden space-y-4 mt-12">
          {executionSteps.map((step, i) => <Collapsible key={step.id} data-index={i} className="border border-border rounded-sm bg-card overflow-hidden shadow-sm group">
              <CollapsibleTrigger className="flex items-center justify-between w-full p-5 text-left [&[data-state=open]>svg]:rotate-180 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset">
                <div className="flex items-center gap-4">
                  <span className="text-3xl font-mono font-black text-primary/20 group-hover:text-primary/40 transition-colors">
                    {step.id}
                  </span>
                  <span className="font-bold text-lg text-card-foreground tracking-tight">
                    {step.title}
                  </span>
                </div>
                <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-300" />
              </CollapsibleTrigger>
              <CollapsibleContent className="px-5 pb-5 pt-0 text-muted-foreground text-sm leading-relaxed data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                <div className="pl-14 border-l-2 border-primary/20 ml-2 py-1">
                  {step.description}
                </div>
              </CollapsibleContent>
            </Collapsible>)}
        </div>
      </div>
    </section>;
}